/**
 * MinimalStorefront — 1:1 layout inspired by ohemaa.vercel.app
 * Uses .theme-minimal CSS variables for full light/dark mode support.
 * Device selection opens a full-screen overlay with media + order form.
 */
import { useState, useMemo, useCallback, useRef, useEffect, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import StorefrontChatWidget from "@/components/StorefrontChatWidget";
import { Package, ArrowLeftRight, CheckCircle, ChevronLeft, ChevronDown, ChevronUp, BookOpen, Smartphone, Watch, Tablet } from "lucide-react";
import { getCategoryLabels, getCategoryOrder, getDeviceDisplayName, getDeviceVariants, formatPrice, calculateFinalPrice, convertFromGHS, getPriceKey, type DeviceCategory, type DeviceModel } from "@/data/catalog";
import { areGradesEnabled, getEffectiveScreenConditions } from "@/data/categorySettingsStore";
import { getDeviceSettings, getActiveDevices, isVariantEnabled, isVariantEnquiryOnly } from "@/stores/deviceSettingsStore";
import { useAdminSettings, getOrderedGrades } from "@/stores/adminStore";
import { useStorefrontSettings } from "@/stores/storefrontSettingsStore";
import { useTenantStore } from "@/lib/tenantContext";
import { useStorefrontTheme } from "@/stores/storefrontThemeStore";
import { useSwapStore } from "@/stores/swapStore";
import { useSync } from "@/components/CloudSyncProvider";
import { useExchangeRate } from "@/hooks/useExchangeRate";
import { getEnabledSubVariantsForDevice } from "@/data/customSubVariantStore";
import OrderForm, { type OrderFormHandle } from "@/components/OrderForm";
import { optimizedImage } from "@/lib/imageOptimize";
import DeviceMediaGallery from "@/components/DeviceMediaGallery";
import DarkModeToggle from "@/components/DarkModeToggle";
import StorefrontThemeSwitcher from "@/components/StorefrontThemeSwitcher";
// Lazy-loaded below-the-fold / interaction-gated widgets — keeps main bundle small for faster TTI/FID.
const StorefrontSearch = lazy(() => import("@/components/StorefrontSearch"));
const FlashSalePopup = lazy(() => import("@/components/FlashSalePopup"));
const DealOfTheWeek = lazy(() => import("@/components/DealOfTheWeek"));
const CustomerReviews = lazy(() => import("@/components/CustomerReviews"));
const AdSlotRenderer = lazy(() => import("@/components/AdSlotRenderer"));
const SponsoredProductsRail = lazy(() => import("@/components/SponsoredProductsRail"));
const FeaturedDevice = lazy(() => import("@/components/FeaturedDevice"));
const TutorialVideoWidget = lazy(() => import("@/components/TutorialVideoWidget"));
const InvalidReferralContact = lazy(() => import("@/components/InvalidReferralContact"));
import { loadConditionGuide, type ConditionGuideEntry } from "@/components/ConditionGuide";
import { useConditionGuideStore } from "@/stores/conditionGuideStore";
import { useFaqStore } from "@/stores/faqStore";
import { useAboutStore } from "@/stores/aboutStore";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HelpCircle, UserCircle, Wifi, WifiOff } from "lucide-react";
import { OrderFormSkeleton, FaqSkeleton, ReviewsSkeleton } from "@/components/StorefrontSkeletons";

const catIconMap: Record<string, typeof Smartphone> = {
  iphone: Smartphone, samsung: Smartphone, "apple-watch": Watch, "samsung-watch": Watch,
  "pixel-watch": Watch, ipad: Tablet, "android-tablet": Tablet,
};

interface MinimalStorefrontProps {
  referralStaff?: string;
  referralInvalid: boolean;
  isStaffLocked: boolean;
  showContactOnly: boolean;
  staffCommissionPercent: number;
  rawRef?: string;
}

const SyncBadge = () => {
  const { isLoaded, lastSyncTime, realtimeDiagnostics: rt } = useSync();
  const [, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick(v => v + 1), 3000); return () => clearInterval(t); }, []);
  if (!isLoaded) return null;
  const ago = lastSyncTime > 0 ? Math.round((Date.now() - lastSyncTime) / 1000) : null;
  const isLive = rt.status === "connected";
  const label = isLive ? (ago !== null && ago < 5 ? "Live" : ago !== null ? `${ago}s ago` : "Connected") : rt.status === "reconnecting" ? "Reconnecting…" : "Offline";
  return (
    <div className="flex items-center justify-center gap-1 mb-1 opacity-80 hover:opacity-100 transition-opacity">
      {isLive ? <Wifi className="w-2.5 h-2.5 text-primary" /> : <WifiOff className="w-2.5 h-2.5 text-foreground/70" />}
      <span className="text-[9px] text-foreground/70">{label}</span>
    </div>
  );
};

function getSubVariantSuffixes(optionGroups: string[][]): string[] {
  if (optionGroups.length === 0) return [""];

  return optionGroups.reduce<string[]>((suffixes, options) => {
    if (options.length === 0) return suffixes;

    return suffixes.flatMap(suffix =>
      options.map(option => (suffix ? `${suffix}|${option}` : option))
    );
  }, [""]);
}

function getVisibleDeviceDisplayPrices({
  device,
  adminSettings,
  sfSettings,
  orderedGrades,
  rate,
  usdRate,
  referralStaff,
  staffCommissionPercent,
}: {
  device: DeviceModel;
  adminSettings: any;
  sfSettings: any;
  orderedGrades: string[];
  rate: number;
  usdRate: number;
  referralStaff?: string;
  staffCommissionPercent?: number;
}): number[] {
  const prices: number[] = [];
  const hiddenCombos = sfSettings.hiddenPriceCombos || {};
  const variants = getDeviceVariants(device).filter(
    v => isVariantEnabled(device.name, v.label) && !isVariantEnquiryOnly(device.name, v.label)
  );
  const ds = getDeviceSettings(device.name);
  const categoryGradesEnabled = areGradesEnabled(device.category);
  const effectiveGrades = categoryGradesEnabled
    ? orderedGrades.filter(g => ds.enabledGrades?.includes(g) ?? true)
    : ["Default"];

  if (categoryGradesEnabled && effectiveGrades.length === 0) effectiveGrades.push(...orderedGrades);

  const screenConds = getEffectiveScreenConditions(device.category, device.name, ds.subconditionsEnabled);
  const screenList = screenConds.length > 0 ? screenConds : [undefined];
  const enabledSubVariants = getEnabledSubVariantsForDevice(device.category, device.name).filter(sv => sv.options.length > 0);
  const subVariantSuffixes = getSubVariantSuffixes(enabledSubVariants.map(sv => sv.options));
  const displayCurrency = adminSettings.displayCurrency || "GHS";

  for (const v of variants) {
    for (const g of effectiveGrades) {
      for (const sc of screenList) {
        const baseKey = getPriceKey(device.name, v.label, g, sc as any);
        const candidateKeys = subVariantSuffixes.map(suffix => (suffix ? `${baseKey}|${suffix}` : baseKey));

        for (const candidateKey of candidateKeys) {
          if (hiddenCombos[candidateKey] || hiddenCombos[baseKey]) continue;

          const overridePrice = adminSettings.priceOverrides?.[candidateKey] ?? adminSettings.priceOverrides?.[baseKey];
          const basePrice = overridePrice ?? v.price;
          if (basePrice <= 0) continue;

          const deviceExtraProfit = adminSettings.deviceExtraProfits?.[candidateKey] ?? adminSettings.deviceExtraProfits?.[baseKey] ?? 0;
          const deviceMargin = adminSettings.deviceMargins?.[candidateKey] ?? adminSettings.deviceMargins?.[baseKey] ?? null;
          const marginToUse = deviceMargin !== null ? deviceMargin : adminSettings.marginPercent;
          const finalPriceGhs = calculateFinalPrice(basePrice, marginToUse, rate, device.usesYuanPricing, deviceExtraProfit);
          if (finalPriceGhs <= 0) continue;

          const perfCommission = referralStaff && (staffCommissionPercent || 0) > 0
            ? Math.round(finalPriceGhs * (staffCommissionPercent || 0) / 100)
            : 0;
          const displayPrice = convertFromGHS(finalPriceGhs + perfCommission, displayCurrency, usdRate, rate);

          if (displayPrice > 0 && !prices.includes(displayPrice)) prices.push(displayPrice);
        }
      }
    }
  }

  return prices.sort((a, b) => a - b);
}

/** Device card with cycling prices */
const DevicePriceCard = ({ device, displayName, variantCount, adminSettings, sfSettings, orderedGrades, rate, usdRate, referralStaff, staffCommissionPercent, onSelect }: {
  device: DeviceModel; displayName: string; variantCount: number;
  adminSettings: any; sfSettings: any; orderedGrades: string[]; rate: number; usdRate: number;
  referralStaff?: string; staffCommissionPercent: number;
  onSelect: (d: DeviceModel) => void;
}) => {
  const [priceIdx, setPriceIdx] = useState(0);

  const allPrices = useMemo(() => {
    return getVisibleDeviceDisplayPrices({
      device,
      adminSettings,
      sfSettings,
      orderedGrades,
      rate,
      usdRate,
      referralStaff,
      staffCommissionPercent,
    });
  }, [device, adminSettings, sfSettings, orderedGrades, rate, usdRate, referralStaff, staffCommissionPercent]);

  useEffect(() => {
    if (allPrices.length <= 1) return;
    const interval = setInterval(() => {
      setPriceIdx(prev => (prev + 1) % allPrices.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [allPrices.length]);

  const currentPrice = allPrices[priceIdx % allPrices.length];

  return (
    <button onClick={() => onSelect(device)}
      className="text-left rounded-2xl p-4 transition-all hover:scale-[1.02] bg-card border border-border">
      <h3 className="text-sm font-semibold mb-1 text-foreground">{displayName}</h3>
      <p className="text-[10px] mb-3 text-muted-foreground">
        {variantCount} variant{variantCount !== 1 ? "s" : ""} available
      </p>
      {currentPrice ? (
        <p className="text-base font-bold mb-2 text-primary font-display transition-all duration-300">{formatPrice(currentPrice)}</p>
      ) : (
        <p className="text-xs mb-2 text-muted-foreground">Price on request</p>
      )}
      {allPrices.length > 1 && (
        <div className="flex gap-0.5 mb-1">
          {allPrices.map((_, i) => (
            <span key={i} className={`w-1 h-1 rounded-full ${i === priceIdx % allPrices.length ? 'bg-primary' : 'bg-muted-foreground/30'}`} />
          ))}
        </div>
      )}
      <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold"
        style={{ background: "hsl(var(--grade-a) / 0.15)", color: "hsl(var(--grade-a))" }}>
        Available
      </span>
    </button>
  );
};

const MinimalStorefront = ({ referralStaff, referralInvalid, isStaffLocked, showContactOnly, staffCommissionPercent, rawRef }: MinimalStorefrontProps) => {
  const [selectedCategory, setSelectedCategory] = useState<DeviceCategory | "">("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<DeviceModel | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const adminSettings = useAdminSettings();
  const sfSettings = useStorefrontSettings();
  const tenant = useTenantStore(s => s.tenant);
  const heroTitle = tenant?.store_title || sfSettings.siteTitle || "Khay's Phones & Premium Devices Pre-Order";
  const heroTagline = tenant?.hero_heading || tenant?.description || sfSettings.siteTagline || "Quality-inspected, professionally graded, competitively priced — so you always know exactly what you're getting.";
  // Subscribe with selectors so only the slices we read trigger re-renders.
  // Reading the whole store object made every theme/icon update rerender the
  // entire storefront, which compounds with adminSettings to cause sluggish
  // category/grade switching.
  const gradingSummaryNote = useStorefrontTheme(s => s.gradingSummaryNote);
  const categorySortOrder = useStorefrontTheme(s => s.categorySortOrder);
  const categoryIcons = useStorefrontTheme(s => s.categoryIcons);
  const mediaGallerySticky = useStorefrontTheme(s => s.mediaGallerySticky);
  const swapSettings = useSwapStore(s => s.settings);
  const { isLoaded } = useSync();
  const { rate, usdRate } = useExchangeRate();
  const orderFormRef = useRef<OrderFormHandle>(null);
  const allFaqs = useFaqStore(s => s.faqs);
  const faqs = useMemo(() => [...allFaqs].filter(f => f.enabled).sort((a, b) => a.order - b.order), [allFaqs]);
  const aboutRaw = useAboutStore(s => s.data);
  const aboutData = useMemo(() => ({ ...aboutRaw, members: [...aboutRaw.members].sort((a, b) => a.order - b.order) }), [aboutRaw]);

  const footerRef = useRef<HTMLElement>(null);

  // Use getOrderedGrades() instead of hardcoded grades — fixes grade visibility bug
  const orderedGrades = useMemo(() => getOrderedGrades(), [adminSettings.customGrades, adminSettings.gradeOrder, adminSettings.deletedDefaultGrades]);

  // Condition guide entries for the Important Notice section
  const conditionGuideRev = useConditionGuideStore(s => s._rev);
  const conditionGuide = useMemo(() => loadConditionGuide(), [conditionGuideRev]);
  const gradeEntries = useMemo(() => conditionGuide.filter(e => e.grade && e.grade !== "__standalone__"), [conditionGuide]);

  const storefrontDisabledCategories = useSwapStore(s => s.settings.storefrontDisabledCategories);
  const allCategoryLabels = useMemo(() => getCategoryLabels(), [adminSettings]);
  const allCategoryOrder = useMemo(
    () => getCategoryOrder().filter(c => !(storefrontDisabledCategories || []).includes(c)),
    [storefrontDisabledCategories, adminSettings]
  );

  const sortedCategories = useMemo(() => {
    if (categorySortOrder.length > 0) {
      const orderMap = new Map(categorySortOrder.map((id, i) => [id, i]));
      return [...allCategoryOrder].sort((a, b) => (orderMap.get(a) ?? 999) - (orderMap.get(b) ?? 999));
    }
    return allCategoryOrder;
  }, [allCategoryOrder, categorySortOrder]);

  const allDevices = useMemo(() => getActiveDevices(), [adminSettings]);

  const devicesForCategory = useMemo(() => {
    if (!selectedCategory) return [];
    return allDevices
      .filter(d => {
        if (d.category !== selectedCategory) return false;
        const ds = getDeviceSettings(d.name);
        if (!ds.enabled) return false;
        const variants = getDeviceVariants(d).filter(v => isVariantEnabled(d.name, v.label));
        if (variants.length === 0) return false;
            return getVisibleDeviceDisplayPrices({
              device: d,
              adminSettings,
              sfSettings,
              orderedGrades,
              rate,
              usdRate,
              referralStaff,
              staffCommissionPercent,
            }).length > 0;
      })
      .sort((a, b) => (getDeviceSettings(a.name).sortOrder || 0) - (getDeviceSettings(b.name).sortOrder || 0));
  }, [selectedCategory, allDevices, adminSettings, sfSettings, orderedGrades, rate, usdRate, referralStaff, staffCommissionPercent]);

  const getDeviceLowestPrice = useCallback((device: DeviceModel): number | null => {
    const prices = getVisibleDeviceDisplayPrices({
      device,
      adminSettings,
      sfSettings,
      orderedGrades,
      rate,
      usdRate,
      referralStaff,
      staffCommissionPercent,
    });
    return prices[0] ?? null;
  }, [adminSettings, rate, usdRate, sfSettings, orderedGrades, referralStaff, staffCommissionPercent]);

  const handleSearchSelect = useCallback((deviceName: string, category: string) => {
    setSelectedCategory(category as DeviceCategory);
    const device = allDevices.find(d => d.name === deviceName);
    if (device) {
      setSelectedDevice(device);
      setDialogOpen(true);
    }
  }, [allDevices]);

  const handleDeviceSelect = useCallback((device: DeviceModel) => {
    setActiveMediaKey("");
    setSelectedDevice(device);
    setDialogOpen(true);
  }, []);

  // When dialog opens, tell OrderForm which device
  useEffect(() => {
    if (dialogOpen && selectedDevice) {
      setTimeout(() => {
        orderFormRef.current?.selectDevice(selectedDevice.category, selectedDevice.name);
      }, 80);
    }
  }, [dialogOpen, selectedDevice]);

  // Listen for "select-device" custom event (from FeaturedDevice widget)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.device) {
        const device = allDevices.find(d => d.name === detail.device);
        if (device) {
          setSelectedCategory(device.category);
          setSelectedDevice(device);
          setDialogOpen(true);
        }
      }
    };
    window.addEventListener("select-device", handler);
    return () => window.removeEventListener("select-device", handler);
  }, [allDevices]);

  // Lock body scroll when dialog is open
  useEffect(() => {
    if (dialogOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [dialogOpen]);

  // The OrderForm is the source of truth for the active media key — it knows
  // the visitor's current variant/grade/screen selection. We mirror it here so
  // the Minimal storefront's gallery picks the same media as Classic.
  const [activeMediaKey, setActiveMediaKey] = useState("");
  const initialMediaKey = useMemo(() => {
    if (!selectedDevice) return "";
    const gradesOn = areGradesEnabled(selectedDevice.category);
    const gradeToUse = gradesOn ? (orderedGrades[0] || "Grade A") : "Default";
    return getPriceKey(selectedDevice.name, "all", gradeToUse);
  }, [selectedDevice, orderedGrades]);
  const mediaKeyForDialog = activeMediaKey || initialMediaKey;

  // ─── Swipe-to-close logic for overlay ───
  const overlayRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number | null>(null);
  const touchStartScrollTop = useRef<number>(0);
  const overlayTranslateY = useRef(0);
  const [swipeDismissing, setSwipeDismissing] = useState(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const scrollEl = overlayRef.current?.querySelector('.overlay-scroll-content') as HTMLElement | null;
    const scrollTop = scrollEl?.scrollTop ?? 0;
    touchStartScrollTop.current = scrollTop;
    if (scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
    } else {
      touchStartY.current = null;
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (touchStartY.current === null) return;
    const scrollEl = overlayRef.current?.querySelector('.overlay-scroll-content') as HTMLElement | null;
    if (scrollEl && scrollEl.scrollTop > 0) {
      touchStartY.current = null;
      return;
    }
    const deltaY = e.touches[0].clientY - touchStartY.current;
    if (deltaY > 0) {
      overlayTranslateY.current = deltaY;
      if (overlayRef.current) {
        overlayRef.current.style.transform = `translateY(${deltaY}px)`;
        overlayRef.current.style.opacity = `${Math.max(0.3, 1 - deltaY / 400)}`;
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchStartY.current === null) return;
    if (overlayTranslateY.current > 120) {
      setSwipeDismissing(true);
      if (overlayRef.current) {
        overlayRef.current.style.transition = 'transform 0.25s ease-out, opacity 0.25s ease-out';
        overlayRef.current.style.transform = 'translateY(100%)';
        overlayRef.current.style.opacity = '0';
      }
      setTimeout(() => {
        setDialogOpen(false);
        setSwipeDismissing(false);
        if (overlayRef.current) {
          overlayRef.current.style.transition = '';
          overlayRef.current.style.transform = '';
          overlayRef.current.style.opacity = '';
        }
      }, 250);
    } else {
      if (overlayRef.current) {
        overlayRef.current.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out';
        overlayRef.current.style.transform = '';
        overlayRef.current.style.opacity = '';
        setTimeout(() => {
          if (overlayRef.current) overlayRef.current.style.transition = '';
        }, 200);
      }
    }
    touchStartY.current = null;
    overlayTranslateY.current = 0;
  }, []);

  // Full-screen lockdown overlay (item 10) — keep all storefront chrome out so the
  // referral/access-restricted form covers the whole viewport on mobile.
  if (showContactOnly) {
    return (
      <div className="theme-minimal min-h-screen-safe bg-background text-foreground flex items-center justify-center px-4 py-8">
        <main className="w-full max-w-xl" aria-label="Storefront access request">
          <Suspense fallback={<div className="min-h-48 rounded-2xl bg-card border border-border/50" />}>
            <InvalidReferralContact
              whatsappNumber={adminSettings.whatsappNumber || "233598429052"}
              reason={referralInvalid ? "invalid_referral" : "access_restricted"}
              showReferrer={referralInvalid || isStaffLocked || !!rawRef}
            />
          </Suspense>
        </main>
      </div>
    );
  }

  return (
    <Suspense fallback={<div className="min-h-screen-safe theme-minimal bg-background text-foreground" />}>
    <div className="min-h-screen-safe theme-minimal bg-background text-foreground">
      <StorefrontThemeSwitcher />
      <TutorialVideoWidget />
      {!showContactOnly && isLoaded && <FlashSalePopup />}
      {!showContactOnly && isLoaded && <DealOfTheWeek referralStaff={referralStaff} staffCommission={0} />}
      <AdSlotRenderer position="before-hero" />

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        {/* ─── Header Card ─── */}
        <div className="rounded-2xl p-6 sm:p-8 relative bg-card border border-border">
          <div className="absolute top-3 right-3">
            <DarkModeToggle compact />
          </div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] mb-2 text-muted-foreground">
            Premium Device Store
          </p>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 text-foreground font-display">
            {heroTitle}
          </h1>
          <p className="text-xs sm:text-sm leading-relaxed text-muted-foreground">
            {heroTagline}
          </p>
        </div>

        <AdSlotRenderer position="after-hero" priority />

        {/* ─── Important Notice + Grades ─── */}
        {!showContactOnly && isLoaded && (gradingSummaryNote || gradeEntries.length > 0) && (
          <div className="rounded-2xl p-5 bg-card border border-border space-y-3">
            <p className="text-sm font-bold text-[hsl(var(--grade-b))]">⚠ Important Notice</p>
            {gradingSummaryNote && (
              <p className="text-xs leading-relaxed text-muted-foreground">{gradingSummaryNote}</p>
            )}
            {/* Inline condition guide */}
            {gradeEntries.length > 0 && sfSettings.gradeGuideEnabled && (
              <>
                <button onClick={() => setGuideOpen(!guideOpen)}
                  className="flex items-center gap-2 text-xs font-semibold text-primary hover:underline py-1">
                  <BookOpen className="w-3.5 h-3.5" />
                  What do device grades mean?
                  {guideOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
                {guideOpen && (
                  <div className="space-y-2 animate-slide-up">
                    {gradeEntries.map(entry => (
                      <div key={entry.grade} className="rounded-lg p-3 bg-secondary/30 border border-border">
                        <h4 className="font-display font-bold text-sm text-foreground mb-1">{entry.grade}</h4>
                        <ul className="space-y-0.5">
                          {entry.description.map((d, i) => {
                            const text = typeof d === "string" ? d : d.text;
                            const bullet = typeof d === "string" ? true : d.bullet !== false;
                            return <li key={i} className="text-xs text-muted-foreground">{bullet ? "• " : ""}{text}</li>;
                          })}
                        </ul>
                        {entry.note && <p className="mt-1 text-[10px] text-muted-foreground italic">{entry.note}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {showContactOnly ? (
          <>
            <AdSlotRenderer position="before-trust" />
            <AdSlotRenderer position="after-trust" />
            <AdSlotRenderer position="before-form" />
            <InvalidReferralContact
              whatsappNumber={adminSettings.whatsappNumber || "233598429052"}
              reason={referralInvalid ? "invalid_referral" : "access_restricted"}
              showReferrer={referralInvalid || isStaffLocked || !!rawRef}
            />
            <AdSlotRenderer position="after-form" />
            <SponsoredProductsRail className="pt-2" />
          </>
        ) : (
          <>
            {/* ─── Action Cards: Track + Swap side by side ─── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-2xl p-5 bg-card border border-border">
                <div className="flex gap-2">
                  <Link to="/track"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all bg-primary text-primary-foreground hover:opacity-90">
                    <CheckCircle className="w-4 h-4" />
                    Check Order Status
                  </Link>
                  {swapSettings.enabled && (
                    <Link to="/swap"
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all bg-secondary text-secondary-foreground border border-border hover:opacity-90"
                      style={swapSettings.buttonColor ? {
                        backgroundColor: swapSettings.buttonColor,
                        color: swapSettings.buttonTextColor || undefined,
                      } : undefined}>
                      <ArrowLeftRight className="w-4 h-4" />
                      {swapSettings.pageLabel || "Swap Device"}
                    </Link>
                  )}
                </div>
              </div>

              <div className="rounded-2xl p-5 bg-card border border-border">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-3 text-muted-foreground">
                  Select Category
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {sortedCategories.map(cat => {
                    const isActive = selectedCategory === cat;
                    const icon = categoryIcons[cat];
                    const CatIcon = catIconMap[cat] || Smartphone;
                    return (
                      <button key={cat} onClick={() => setSelectedCategory(cat as DeviceCategory)}
                        className={`flex flex-col items-center justify-center gap-1.5 rounded-xl px-2 py-3 text-[10px] font-medium transition-all border-2 ${
                          isActive ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:border-primary/50"
                        }`}>
                        {/* Fixed aspect-ratio frame so custom uploads keep their natural ratio
                            and don't appear cropped/zoomed-in on the minimal storefront. */}
                        <div className="w-10 h-10 flex items-center justify-center overflow-hidden">
                          {icon ? (
                            <img src={optimizedImage(icon, { width: 80, height: 80 })} alt={allCategoryLabels[cat] || cat} width={40} height={40} className="max-w-full max-h-full w-auto h-auto object-contain" loading="lazy" />
                          ) : (
                            <CatIcon className="w-6 h-6" />
                          )}
                        </div>
                        <span className="truncate w-full text-center">{allCategoryLabels[cat] || cat}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ─── Search Bar ─── */}
            <StorefrontSearch onSelect={handleSearchSelect} />

            {/* ─── Device Grid ─── */}
            {selectedCategory && (
              <>
                <AdSlotRenderer position="before-featured" />
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-foreground font-display">Choose Device</h2>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold text-primary">{allCategoryLabels[selectedCategory] || selectedCategory}</span>
                  </p>
                </div>

                {devicesForCategory.length === 0 ? (
                  <div className="rounded-2xl p-8 text-center bg-card border border-border">
                    <p className="text-sm text-muted-foreground">No devices available in this category</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {devicesForCategory.map(device => {
                      const displayName = getDeviceDisplayName(device.name);
                      const variants = getDeviceVariants(device).filter(v => isVariantEnabled(device.name, v.label));
                      return (
                        <DevicePriceCard
                          key={device.name}
                          device={device}
                          displayName={displayName}
                          variantCount={variants.length}
                          adminSettings={adminSettings}
                          sfSettings={sfSettings}
                          orderedGrades={orderedGrades}
                          rate={rate}
                          usdRate={usdRate}
                          referralStaff={referralStaff}
                          staffCommissionPercent={staffCommissionPercent}
                          onSelect={handleDeviceSelect}
                        />
                      );
                    })}
                  </div>
                )}
                <AdSlotRenderer position="after-featured" />
              </>
            )}

            {/* ─── Reviews ─── */}
            {!isLoaded ? (
              adminSettings.reviewsOnStorefront ? <ReviewsSkeleton /> : null
            ) : (
              adminSettings.reviewsOnStorefront && <CustomerReviews showAll maxItems={8} />
            )}

            {/* ─── FAQ ─── */}
            {!isLoaded ? (
              sfSettings.faqEnabled ? <FaqSkeleton /> : null
            ) : (
              sfSettings.faqEnabled && faqs.length > 0 && (
                <>
                  <AdSlotRenderer position="before-faq" />
                  <section className="rounded-2xl p-4 sm:p-6 bg-card border border-border">
                    <div className="flex items-center gap-2 mb-3">
                      <HelpCircle className="w-4 h-4 text-primary" />
                      <h2 className="text-base font-bold text-foreground font-display">{sfSettings.faqTitle}</h2>
                    </div>
                    <Accordion type="single" collapsible className="w-full">
                      {faqs.map((faq) => (
                        <AccordionItem key={faq.id} value={faq.id} className="border-border">
                          <AccordionTrigger className="text-xs font-semibold text-foreground text-left">{faq.question}</AccordionTrigger>
                          <AccordionContent>
                            <p className="text-xs text-muted-foreground">{faq.answer || "Answer coming soon."}</p>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </section>
                  <AdSlotRenderer position="after-faq" />
                </>
              )
            )}
          </>
        )}
      </main>

      <AdSlotRenderer position="before-footer" />
      {/* ─── Footer with About Us (same as classic) ─── */}
      <footer ref={footerRef} className="py-6 px-4 border-t border-border bg-card">
        {!showContactOnly && (aboutData.storeDescription || aboutData.members.length > 0) && (
          <div className="max-w-5xl mx-auto mb-5">
            <div className="flex items-center gap-2 mb-3 justify-center">
              <UserCircle className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-bold text-foreground font-display">About Us</h2>
            </div>
            {aboutData.storeDescription && (
              <p className="text-xs text-muted-foreground text-center mb-4">{aboutData.storeDescription}</p>
            )}
            {aboutData.members.length > 0 && (() => {
              const groups = aboutData.groups || [];
              const membersByGroup: Record<string, typeof aboutData.members> = {};
              const ungrouped: typeof aboutData.members = [];
              aboutData.members.forEach(m => {
                if (m.group && groups.includes(m.group)) {
                  if (!membersByGroup[m.group]) membersByGroup[m.group] = [];
                  membersByGroup[m.group].push(m);
                } else {
                  ungrouped.push(m);
                }
              });
              const renderMember = (member: typeof aboutData.members[0]) => (
                <div key={member.id} className="text-center space-y-1.5">
                  <div className="w-14 h-14 rounded-full bg-accent flex items-center justify-center mx-auto overflow-hidden">
                    {member.imageUrl ? (
                      <img src={optimizedImage(member.imageUrl, { width: 56, height: 56, resize: "cover" })} alt={member.name} loading="lazy" width={56} height={56} className="w-full h-full object-cover" />
                    ) : (
                      <UserCircle className="w-8 h-8 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-foreground">{member.name}</p>
                    <p className="text-[10px] text-primary font-medium">{member.role}</p>
                    {member.bio && <p className="text-[10px] text-muted-foreground mt-0.5">{member.bio}</p>}
                  </div>
                </div>
              );
              return (
                <details open={typeof window !== "undefined" && window.matchMedia("(min-width: 640px)").matches} className="group rounded-xl border border-border/50 bg-background/50 overflow-hidden">
                  <summary className="flex items-center justify-center gap-2 px-3 py-2.5 cursor-pointer list-none select-none hover:bg-accent/50 transition-colors [&::-webkit-details-marker]:hidden">
                    <UserCircle className="w-3.5 h-3.5 text-primary" />
                    <span className="text-xs font-bold text-foreground">Meet the Team</span>
                    <span className="text-[10px] text-muted-foreground">({aboutData.members.length})</span>
                    <svg className="w-3 h-3 text-muted-foreground transition-transform group-open:rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
                  </summary>
                  <div className="p-3 space-y-4">
                    {groups.map(g => {
                      const members = membersByGroup[g];
                      if (!members || members.length === 0) return null;
                      return (
                        <div key={g}>
                          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide text-center mb-2">{g}</p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{members.map(renderMember)}</div>
                        </div>
                      );
                    })}
                    {ungrouped.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{ungrouped.map(renderMember)}</div>
                    )}
                  </div>
                </details>
              );
            })()}
          </div>
        )}
        <SyncBadge />
        <p className="text-[10px] text-muted-foreground text-center">
          © 2026 Khay's Phones and Premium Devices Pre-Order LTD. All prices in Ghana Cedis (GH₵).
        </p>
      </footer>

      {!showContactOnly && <FeaturedDevice />}

      {/* ─── Full-Screen Device Detail Overlay (swipe-to-close) ─── */}
      {dialogOpen && selectedDevice && (
        <div
          ref={overlayRef}
          className="fixed inset-0 z-50 bg-background flex flex-col animate-slide-up"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Swipe indicator */}
          <div className="flex justify-center pt-2 pb-1 flex-shrink-0">
            <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
          </div>

          {/* Overlay header */}
          <div className="flex items-center justify-between px-4 py-2 bg-card border-b border-border flex-shrink-0">
            <button onClick={() => setDialogOpen(false)} className="p-2 rounded-lg hover:bg-accent transition-colors">
              <ChevronLeft className="w-5 h-5 text-foreground" />
            </button>
            <h2 className="text-sm font-bold text-foreground font-display truncate mx-3">
              {getDeviceDisplayName(selectedDevice.name)}
            </h2>
            <DarkModeToggle compact />
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto overlay-scroll-content">
            {/* Media gallery */}
            <DeviceMediaGallery
              deviceName={selectedDevice.name}
              category={selectedDevice.category}
              mediaKey={mediaKeyForDialog}
            />

            {/* Order form (compact — no category/model selectors, no condition guide, no gallery) */}
            <div className="px-4 py-4">
              <OrderForm
                ref={orderFormRef}
                compact
                referralStaff={referralStaff}
                staffCommissionPercent={staffCommissionPercent}
                onMediaKeyChange={setActiveMediaKey}
              />
            </div>
          </div>
        </div>
      )}
      <StorefrontChatWidget />
    </div>
    </Suspense>
  );
};

export default MinimalStorefront;
